// Resource module — training lifecycle (/training). React-free.
// Post-envelope wire shapes: list → { jobs; total } (total = filtered org
// count, NOT capped by limit); detail → { job, progress, status }
// (multi-part payload — the fetcher returns it whole so the page's raw viewer
// keeps every sibling); cancel/resume → { job }.
import { apiFetch } from '@/lib/api'
import type { components } from '@/types/openapi'
import type { TrainingJob, TrainingState } from '@/types/api'

export type TrainingLimits = components['schemas']['TrainingLimits']

/** GET /training/jobs/:id data payload — the job row plus its live siblings
 *  (frontend-contract §7.4 "GET job returns progress+status"). The mock also
 *  inlines progress/progressPercent onto the job row itself; siblings stay
 *  optional because the wire contract doesn't guarantee either. */
export type TrainingJobDetail = {
  job: TrainingJob & { progress?: Record<string, unknown> | null; progressPercent?: number | null }
  progress?: Record<string, unknown> | null
  status?: { state?: TrainingState; elapsedSeconds?: number } | null
}

// Key contract: [domain, orgId, leaf, ...params] — see OrgQueryKey in @/lib/queries.
// `all` is the domain prefix: invalidating it refreshes jobs lists, detail,
// recent and palette copies for the org in one shot.
export const trainingKeys = {
  all: (orgId: string | null) => ['training', orgId] as const,
  jobs: (orgId: string | null) => ['training', orgId, 'jobs'] as const,
  /** Filtered list — the state element sits under 'jobs' so the `jobs` prefix
   *  covers every filter variant. */
  jobsFiltered: (state: string) => (orgId: string | null) => ['training', orgId, 'jobs', state] as const,
  /** Per-state count probe — fetches ?state=<s>&limit=1 and reads only
   *  `total`. The 'stateTotal' element keeps it distinct from
   *  jobsFiltered(state) (same state slot, different arity) while staying
   *  under the 'jobs' prefix for invalidation. */
  stateTotal: (state: string) => (orgId: string | null) => ['training', orgId, 'jobs', 'stateTotal', state] as const,
  recent: (orgId: string | null) => ['training', orgId, 'recent'] as const,
  palette: (orgId: string | null) => ['training', orgId, 'palette'] as const,
  job: (jobId: string) => (orgId: string | null) => ['training', orgId, 'job', jobId] as const,
  /** Org plan + concurrency limit (GET /training/limits) — usage-meter read surface. */
  limits: (orgId: string | null) => ['training', orgId, 'limits'] as const,
}

/** Peeled shape of GET /training/jobs — rows + the same-filter org total. */
export type TrainingJobListPage = { jobs: TrainingJob[]; total: number }

export function fetchTrainingJobs(
  orgId: string,
  params?: { state?: string; limit?: number | string },
): Promise<TrainingJobListPage> {
  return apiFetch<TrainingJobListPage>('/training/jobs', { orgId, params })
}

export function fetchTrainingJob(orgId: string, jobId: string): Promise<TrainingJobDetail> {
  return apiFetch<TrainingJobDetail>(`/training/jobs/${jobId}`, { orgId })
}

export async function createTrainingJob(orgId: string, payload: Record<string, unknown>): Promise<TrainingJob> {
  return (await apiFetch<{ job: TrainingJob }>('/training/jobs', { method: 'POST', orgId, body: JSON.stringify({ orgId, ...payload }) })).job
}

export function cancelTrainingJob(orgId: string, jobId: string): Promise<unknown> {
  return apiFetch(`/training/jobs/${jobId}/cancel`, { method: 'POST', orgId, body: JSON.stringify({ orgId }) })
}

export function resumeTrainingJob(orgId: string, jobId: string): Promise<unknown> {
  return apiFetch(`/training/jobs/${jobId}/resume`, { method: 'POST', orgId, body: JSON.stringify({ orgId }) })
}

// GET /training/limits — {limits: TrainingLimits} keyed payload, dug explicitly.
export async function fetchTrainingLimits(orgId: string): Promise<TrainingLimits> {
  return (await apiFetch<{ limits: TrainingLimits }>('/training/limits', { orgId })).limits
}
